/**
 * Offer Path Library
 * This library is used to track and propogate the offer path chosen by the user. Once a path is created, an offerId is returned.
 * The offerId is attached to the URL and each subsequent page load uses this Id to reload the path data.
 */
define('components/offers',[
    "jquery",
    "logging",
    "storage",
    "utilities"
], function($, logging, storage, utilities) {
    "use strict";

    var _ModuleName = "RegPath Offers";
    var _config = {
        params: {
            offerId: 'offerId',
            exclude: ['utm_medium']
        },
        storage: {
            regpath: "offers-"
        }
    };
    var _default = {
        valid: false,
        offerId: "",
        landingPage: "",
        paths: [],
        currentOffer: ""
    };
    var _data = _default;

    /**
     * Get next offer in current path
     * @param {obj} $form jQuery object representing the form with field data to get added as query params
     * @returns {string} url to next offer
     */
    var next = function($form) {
        // If data loaded successfully, get the next offer
        if(_data.valid) {
            _data.currentOffer++; // Increment to next offer
            if(!_saveOfferData(_data)) { // Store updated data
                logging.warn("Error saving updates while getting next offer", _ModuleName);
            }

            var nextOffer = _data.paths[_data.currentOffer]; // Return current offer path
            var queryString = _getMergedQuery(window.location.search, _data.offerId);
            if(queryString) {
                nextOffer += (nextOffer.indexOf('?') < 0 ? '?' : '&') + queryString;
            }
            logging.debug("Next Offer: " + nextOffer, _ModuleName);
            return nextOffer;
        }

        logging.error("Failed to get next offer, no valid offer data loaded", _ModuleName);
        return '';
    }

    /**
     * Create Offer Path
     * @param {array} paths List of urls to offers
     * @returns {string} string representing the offer data id. Empty string is returned if there was a problem.
     */
    var create = function(paths, landingPage) {
        _data.offerId = _getNewOfferId();
        _data.paths = paths;
        _data.landingPage = landingPage;
        _data.currentOffer = -1;
        _data.valid = true;

        if(_saveOfferData(_data)) {
            logging.debug("Succesfully created and saved offer data with id " + _data.offerId, _ModuleName);
            return _data.offerId;
        }

        logging.error("Something went wrong creating/saving data", _ModuleName);
        return '';
    }

    /**
     * Load offer data with associated Id
     * @param {string} offerId string representing unique offer id for current path
     * @returns {bool} true if data loaded successfully, false otherwise
     */
    var load = function(offerId) {
        // If offer id is not passed in, try  getting on from the query parameters
        if(!offerId) {
            offerId = utilities.getParameter(_config.params.offerId);
        }
        // If we still have no offer id, return an error
        if(!offerId) {
            logging.error("Error loading offer. No valid offer Id found", _ModuleName);
            return false;
        }

        logging.debug("Loading offer path data for id " + offerId, _ModuleName);
        _loadOfferData(offerId);

        // Check if data loaded is marked valid
        if(!_data.valid) { 
            logging.error("Error loading offer. Data retrieved not marked valid", _ModuleName);
            return false;
        }

        logging.debug("Successfully loaded offer data", _ModuleName);
        return true;
    }

    /**
     * Check if we are on the final offer
     * @returns {bool} returns true if this is the final offer in the path, false otherwise
     */
    var isFinalOffer = function() {
        if(_data.valid) {
            return _data.currentOffer == _data.paths.length - 1;
        }
        return false;
    }

    /**
     * Gets the configured landing page
     * @returns {string} final landing page if valid configuration, empty string otherwise
     */
    var getLandingPage = function() {
        if(_data.valid) {
            return _data.landingPage;
        }
        return '';
    }

    /**
     * Creates unique offer id for current path
     */
    var _getNewOfferId = function() {
        return 'xxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Loads offer path data from session storage
     * @param {string} offerId unique id representing offer path
     * @returns {bool} true of sucessfully loaded valid data, false otherwise
     */
    var _loadOfferData = function(offerId) {
        _data = storage.getSessionStorage(_config.storage.regpath + offerId);
        // Make sure data loaded succesfully
        if(_data && _data.valid) return true;
        // If not successful, set to default and return false
        _data = _default;
        return false;
    }

    /**
     * Saves current offer path data if valid
     * @returns {bool} true if successful, false otherwise
     */
    var _saveOfferData = function() {
        if(_data.valid) {
            return storage.setSessionStorage(_config.storage.regpath + _data.offerId, _data);
        }

        return false;
    }

    /**
     * grabs form data from frm and merges it into the query string.
     * @param {string} querystring querystring of current page
     * @param {obj} form form object to get field data
     * @param {string} offerId offer id of current path
     */
    var _getMergedQuery = function(querystring, offerId) {
        var result = {};

        // Add query string values
        $.each(querystring.substring(1).replace(/\+/gi, ' ').split('&'), function (idx, val) {
            var pair = val.split('=');
            if (pair && pair.length == 2 && _config.params.exclude.indexOf(pair[0]) === -1)
                result[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
        });

        // Add offer id to continue path
        result[_config.params.offerId] = offerId;

        return $.param(result);
    }

    return {
        create: create,
        load: load,
        next: next,
        isFinalOffer: isFinalOffer,
        getLandingPage: getLandingPage
    }
});
